import { configureStore } from '@reduxjs/toolkit';
import { setAutoFreeze } from 'immer';
import type { Middleware } from '@reduxjs/toolkit';
import layoutReducer from '../features/layout/layoutSlice';
import selectionReducer from '../features/selection/selectionSlice';
import historyReducer from '../features/history/historySlice';
import uiReducer from '../features/ui/uiSlice';
import validationReducer from '../features/validation/validationSlice';
import floorplanReducer from '../features/floorplan/floorplanSlice';
import facilityReducer from '../features/facility/facilitySlice';
import layersReducer from '../features/layers/layersSlice';
import { addHistoryAction } from '../features/history/historySlice';
import { redoInvalidationMiddleware } from '../features/history/redoInvalidation';
import type { LayoutElement } from '../types';
import { generateId } from '../utils/geometry';
import simulationReducer from '../features/simulation/simulationSlice';
import layout3dReducer from '../features/layout3d/layout3dSlice';
import toolboxReducer from '../features/toolbox/toolboxSlice';
import equipmentReducer from '../features/equipment/equipmentSlice';
import authReducer from '../features/auth/authSlice';
import mindmapReducer from '../features/mindmap/mindmapSlice';
import dxfImportReducer from '../features/dxfImport/dxfImportSlice';
import { simulationClockStore } from './simulationClockStore';
import { connectorShiftStore } from './connectorShiftStore';
import { junctionTurntableStore } from './junctionTurntableStore';
import { railTransferPoseStore } from './railTransferPoseStore';
import { transferWallHoldStore } from './transferWallHoldStore';
import { playbackDelayStore } from './playbackDelayStore';
import { liveOhtRuntimeStore } from './liveOhtRuntimeStore';
import { transferEquipmentDetailHoldStore } from './transferEquipmentDetailHoldStore';
import { stockerRackDisplayStore } from '../components/canvas/amhs/Stocker/stockerRackDisplayStore';
import { transferAnimationPipelineStore } from './transferAnimationPipelineStore';
import { liveMotionStore } from './liveMotionStore';
import { resetStocker2dDisplayState } from '../features/simulation/stocker2dDisplayCounts';

// An imported floor plan can contain hundreds of thousands of polyline points.
// Immer's dev-mode auto-freeze would deep-freeze that whole array on every
// dispatch, which is prohibitively slow, so we opt out of auto-freezing.
setAutoFreeze(false);

const cloneElement = (element: LayoutElement): LayoutElement => ({
  ...element,
  position: { ...element.position },
  dimensions: { ...element.dimensions },
  metadata: { ...element.metadata },
  customProperties: element.customProperties ? { ...element.customProperties } : undefined,
});

const historyTrackedActions = new Set([
  'layout/addElement',
  'layout/addElements',
  'layout/duplicateElements',
  'layout/updateElement',
  'layout/updateElements',
]);

const historyCaptureMiddleware: Middleware = (api) => (next) => (action) => {
  const typedAction = action as { type?: string; meta?: { skipHistory?: boolean } };
  const actionType = typedAction.type || '';
  const shouldTrack = historyTrackedActions.has(actionType) && !typedAction.meta?.skipHistory;

  const prevState = shouldTrack ? (api.getState() as RootState) : null;
  const result = next(action);

  if (!shouldTrack || !prevState) return result;

  const nextState = api.getState() as RootState;
  const prevElements = prevState.layout.elements;
  const nextElements = nextState.layout.elements;

  if (
    actionType === 'layout/addElement' ||
    actionType === 'layout/addElements' ||
    actionType === 'layout/duplicateElements'
  ) {
    const addedIds = Object.keys(nextElements).filter((id) => !prevElements[id]);
    if (addedIds.length > 0) {
      api.dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'add',
          timestamp: Date.now(),
          elementIds: addedIds,
          newState: addedIds.map((id) => cloneElement(nextElements[id])),
        })
      );
    }
    return result;
  }

  if (actionType === 'layout/updateElement' || actionType === 'layout/updateElements') {
    const changedIds = Object.keys(nextElements).filter(
      (id) => prevElements[id] && prevElements[id] !== nextElements[id]
    );
    if (changedIds.length > 0) {
      api.dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'update',
          timestamp: Date.now(),
          elementIds: changedIds,
          previousState: changedIds.map((id) => cloneElement(prevElements[id])),
          newState: changedIds.map((id) => cloneElement(nextElements[id])),
        })
      );
    }
  }

  return result;
};

/** Keep the RAF clock aligned when UI seeks / starts / stops playback. */
const CLOCK_SYNC_ACTIONS = new Set([
  'simulation/seekSimulationTime',
  'simulation/nudgeSimulationTime',
  'simulation/stopSimulation',
  'simulation/startSimulation',
  'simulation/restartSimulation',
  'simulation/loadSimulationResponse',
]);

/**
 * Multi-FOUP (esp. gap) crosses H-connectors / turntables many times. Stale
 * transfer anims from a prior play (or a mid-run seek) pin OHTs until the user
 * scrubs past the old startedAt — clear them whenever the clock is forced.
 */
const TRANSFER_RESET_ACTIONS = new Set([
  'simulation/seekSimulationTime',
  'simulation/nudgeSimulationTime',
  'simulation/stopSimulation',
  'simulation/startSimulation',
  'simulation/restartSimulation',
  'simulation/loadSimulationResponse',
  'simulation/resetLiveStream',
]);

const simulationClockMiddleware: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  const typedAction = action as { type?: string };
  const type = typedAction.type;
  if (type && TRANSFER_RESET_ACTIONS.has(type)) {
    // Drop stale H-connector / turntable overrides so multi-FOUP (gap) playback
    // does not stay pinned until the user scrubs past an old startedAt.
    connectorShiftStore.reset();
    junctionTurntableStore.reset();
    railTransferPoseStore.reset();
    transferWallHoldStore.reset();
    playbackDelayStore.reset();
    transferEquipmentDetailHoldStore.reset();
    liveMotionStore.reset();
    resetStocker2dDisplayState();
    liveOhtRuntimeStore.reset();
    stockerRackDisplayStore.reset();
    transferAnimationPipelineStore.reset();
  }
  if (type && CLOCK_SYNC_ACTIONS.has(type)) {
    const time = (api.getState() as RootState).simulation.simulationTime;
    simulationClockStore.setTime(time);
  }
  return result;
};

/**
 * Simulation state the development-only Redux checks must not walk.
 *
 * `serializableCheck` and `immutableCheck` deep-traverse the whole store on
 * every dispatch. A running stream holds every vehicle leg, transfer event and
 * OHT timeline of the run here, and dispatches a batch about ten times a
 * second, so the traversal was measured taking 762ms (serializable) and 1622ms
 * (immutable) per action — seconds of blocked main thread, which is what makes
 * a dense layout stutter no matter how evenly the API delivers.
 *
 * These are the same reasons the floor-plan polylines are already exempt. The
 * data is produced by the stream reducers and read by the renderers; nothing
 * mutates it in place, so the checks only cost.
 */
const SIMULATION_HOT_PATHS = [
  'simulation.browserKinetic',
  'simulation.replayPlan',
  'simulation.playbackVehicles',
  'simulation.transportRequests',
  'simulation.assignments',
  'simulation.amhsReports',
  'simulation.live.ohtTimelines',
  'simulation.live.inventory',
  'simulation.live.collisions',
];

export const store = configureStore({
  reducer: {
    layout: layoutReducer,
    selection: selectionReducer,
    history: historyReducer,
    ui: uiReducer,
    validation: validationReducer,
    floorplan: floorplanReducer,
    facility: facilityReducer,
    layers: layersReducer,
    simulation: simulationReducer,
    layout3d: layout3dReducer,
    toolbox: toolboxReducer,
    equipment: equipmentReducer,
    auth: authReducer,
    mindmap: mindmapReducer,
    dxfImport: dxfImportReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        warnAfter: 128,
        // Ignore these action types. The floor-plan payload is huge (potentially
        // hundreds of thousands of points), so skip serializability scanning.
        ignoredActions: [
          'history/addHistoryAction',
          'floorplan/setFloorPlan',
          'facility/setFacility',
          'mindmap/setMindmapDocument',
          // Stream payloads carry the run's legs and events, at stream rate.
          'simulation/loadSimulationResponse',
          'simulation/applyLiveReplicaBatch',
          'simulation/applyLiveSnapshot',
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: [
          'payload.timestamp',
          'payload.polylines',
          'payload.polylineLayers',
          'payload.polylineMeta',
          'payload.labels',
          'payload.regions',
          'payload.document',
        ],
        // Ignore these paths in the state
        ignoredPaths: [
          'history.past',
          'history.future',
          'floorplan.polylines',
          'floorplan.polylineLayers',
          'floorplan.polylineMeta',
          'floorplan.labels',
          'floorplan.hiddenEntityTypes',
          'facility.regions',
          'equipment.history',
          'mindmap.document',
          'mindmap.markers',
          'mindmap.relatedIds',
          ...SIMULATION_HOT_PATHS,
        ],
      },
      // Scanning a 200k-point array for mutations on every dispatch is too slow.
      immutableCheck: {
        warnAfter: 128,
        ignoredPaths: [
          'floorplan.polylines',
          'floorplan.polylineLayers',
          'floorplan.polylineMeta',
          'floorplan.labels',
          'facility.regions',
          'equipment.history',
          'mindmap.document',
          'mindmap.markers',
          'mindmap.relatedIds',
          ...SIMULATION_HOT_PATHS,
        ],
      },
    }).concat(historyCaptureMiddleware, redoInvalidationMiddleware, simulationClockMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
